// frontend/src/modules/tableNormalization.js
const DEFAULT_TABLE_NOTE = '数据表';

export const normalizeCarTableNote = (table = {}, index = 0) => {
  // 标准化车辆表格备注并补齐默认note。
  const normalized = JSON.parse(JSON.stringify(table));
  const metadata = normalized.metadata || {};
  normalized.metadata = {
    ...metadata,
    note: (metadata.note || `${DEFAULT_TABLE_NOTE}${index + 1}`).trim(),
  };
  return normalized;
};

export const normalizeTables = (tables = [], noteNormalizer = normalizeCarTableNote) =>
  // 按指定规则批量标准化表格数组。
  (Array.isArray(tables) ? tables : []).map((table, index) => noteNormalizer(table, index));

export const normalizeSingleResultTables = (result) =>
  // 将单个对象或数组统一转换为数组结构。
  Array.isArray(result) ? result : (result ? [result] : []);

const normalizeLabel = (value = '') =>
  String(value || '')
    .toLowerCase()
    .replace(/[^\u4e00-\u9fa5a-z0-9]/g, '');

const tokenize = (value = '') => {
  const normalized = normalizeLabel(value);
  if (!normalized) return [];
  const latinTokens = normalized.match(/[a-z0-9]+/g) || [];
  const chineseTokens = normalized.match(/[\u4e00-\u9fa5]/g) || [];
  return [...latinTokens, ...chineseTokens];
};

const jaccardSimilarity = (a = '', b = '') => {
  const tokensA = new Set(tokenize(a));
  const tokensB = new Set(tokenize(b));
  if (!tokensA.size || !tokensB.size) return 0;
  let intersection = 0;
  tokensA.forEach((token) => {
    if (tokensB.has(token)) intersection += 1;
  });
  const unionSize = new Set([...tokensA, ...tokensB]).size;
  return unionSize ? intersection / unionSize : 0;
};

const getTableClassName = (table = {}) =>
  table?.metadata?.matched_table_title
  || table?.metadata?.source_table_title
  || table?.title
  || table?.metadata?.note
  || '未命名表';

const getFrequencyColumn = (columns = []) =>
  (Array.isArray(columns) ? columns : []).find((col) => /freq|频率/i.test(String(col || '')));

const toNumeric = (value) => {
  const parsed = Number(String(value ?? '').replace(/,/g, '').trim());
  return Number.isFinite(parsed) ? parsed : null;
};

const getValueColumn = (columns = [], frequencyColumn = '') => {
  const candidates = (Array.isArray(columns) ? columns : []).filter(
    (col) => col !== frequencyColumn && col !== '序号' && col !== '删除'
  );
  return candidates.find((col) => /factor|限值|db|multip|校准因子|增益|损耗|系数|阻抗/i.test(String(col || '')))
    || candidates[0]
    || '';
};

const getGroupKey = (table, existingGroups, threshold = 0.55) => {
  const className = getTableClassName(table);
  const normalizedClassName = normalizeLabel(className);
  const exactMatch = existingGroups.find(
    (group) => normalizeLabel(group.className) === normalizedClassName
  );
  if (exactMatch) return exactMatch.className;

  let bestGroup = null;
  let bestScore = 0;
  existingGroups.forEach((group) => {
    const score = jaccardSimilarity(className, group.className);
    if (score > bestScore) {
      bestScore = score;
      bestGroup = group;
    }
  });

  return bestGroup && bestScore >= threshold ? bestGroup.className : className;
};

export const mergeBatchResultItems = (items = []) => {
  // 批量结果按“高相似类名”合并成对比表，并增加差值列。
  const safeItems = Array.isArray(items) ? items : [];
  const flattenedTables = safeItems.flatMap((item, fileIndex) => {
    if (!Array.isArray(item?.result)) {
      return [];
    }

    return item.result.map((table, tableIndex) => ({
      ...table,
      metadata: {
        ...(table?.metadata || {}),
        source_file: item.filename || `文件${fileIndex + 1}`,
        source_file_index: fileIndex,
        source_table_index: tableIndex,
      },
    }));
  });

  if (!flattenedTables.length) {
    return [];
  }

  const groups = [];
  flattenedTables.forEach((table) => {
    const groupKey = getGroupKey(table, groups);
    const found = groups.find((group) => group.className === groupKey);
    if (found) {
      found.tables.push(table);
      return;
    }
    groups.push({ className: groupKey, tables: [table] });
  });

  return groups.map((group, groupIndex) => {
    const allFileNames = [...new Set(group.tables.map((table) => table?.metadata?.source_file).filter(Boolean))];
    const frequencyColumn = getFrequencyColumn(group.tables[0]?.columns || []) || 'Frequency[MHz]';
    const valueColumn = getValueColumn(group.tables[0]?.columns || [], frequencyColumn) || 'Value';
    const perFileValueColumns = allFileNames.map((fileName) => `${valueColumn}_${fileName}`);
    const mergedRowsMap = new Map();

    group.tables.forEach((table) => {
      const fileName = table?.metadata?.source_file || '未知文件';
      const tableFrequencyColumn = getFrequencyColumn(table?.columns || []) || frequencyColumn;
      const tableValueColumn = getValueColumn(table?.columns || [], tableFrequencyColumn) || valueColumn;
      (table?.rows || []).forEach((row) => {
        const freq = row?.[tableFrequencyColumn];
        if (freq === undefined || freq === null || String(freq).trim() === '') {
          return;
        }
        const frequencyKey = String(freq).trim();
        if (!mergedRowsMap.has(frequencyKey)) {
          mergedRowsMap.set(frequencyKey, { [frequencyColumn]: freq });
        }
        const mergedRow = mergedRowsMap.get(frequencyKey);
        mergedRow[`${valueColumn}_${fileName}`] = row?.[tableValueColumn] ?? null;
      });
    });

    const mergedRows = Array.from(mergedRowsMap.values())
      .sort((a, b) => {
        const av = toNumeric(a[frequencyColumn]);
        const bv = toNumeric(b[frequencyColumn]);
        if (av === null || bv === null) return String(a[frequencyColumn]).localeCompare(String(b[frequencyColumn]));
        return av - bv;
      })
      .map((row, index) => {
        const completedRow = { ...row };
        perFileValueColumns.forEach((col) => {
          if (!(col in completedRow)) {
            completedRow[col] = null;
          }
        });
        if (perFileValueColumns.length >= 2) {
          const valueA = toNumeric(completedRow[perFileValueColumns[0]]);
          const valueB = toNumeric(completedRow[perFileValueColumns[1]]);
          completedRow['差值(文件1-文件2)'] =
            valueA !== null && valueB !== null ? Number((valueA - valueB).toFixed(4)) : null;
        }
        return completedRow;
      });

    const diffColumn = perFileValueColumns.length >= 2 ? ['差值(文件1-文件2)'] : [];
    return {
      columns: [frequencyColumn, ...perFileValueColumns, ...diffColumn],
      rows: mergedRows,
      metadata: {
        note: `批量对比-${group.className}`,
        matched_table_title: group.className,
        source_files: allFileNames,
        group_index: groupIndex,
      },
    };
  });
};